import {css, html, nothing, TemplateResult} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {Activity} from './activity';
import Decimal from './break_infinity';
import global_css from './global-css';
import {evaluateCost} from './interfaces/dynamic-value';
import {IQueueable} from './interfaces/queueable';
import {IValuable} from './interfaces/valuable';
import {ToolTippy} from './mixins/tooltippy';
import {ri} from './runner-idle';
import {UnlockableLitElement} from './unlockable';
import {formatCostEffect, formatEng, formatTime} from './utils';

@customElement('ri-spell')
export class Spell extends Activity {
  @property() override name = 'spell';
  @property() override displayName = 'Spell';
  override _tags = new Set(['spell']);
  @property({type: Number}) override optionIndex = 0;
  @property({type: Number}) override length = 1;
  @property({type: Decimal}) cooldown: Decimal = new Decimal(1);
  _currentCooldown: Decimal = new Decimal(0);
  level = 1;
  get currentCooldown(): Decimal {
    return this._currentCooldown;
  }
  set currentCooldown(value: Decimal) {
    this._currentCooldown = value.clamp(0, this.cooldown);
    this.requestUpdate();
  }
  @property({type: Decimal}) override uses = new Decimal(0);
  @property({type: Boolean, reflect: true}) active = false;
  @property() override cost = new Map();
  @property() getCostString = () => '';
  @property() override effect = new Map();
  @property() getEffectString = () => '';
  // @property() override canAfford: (
  //   step: number,
  //   optionIndex: number
  // ) => boolean = (step, optionIndex) => {
  //   for (const [key, {value, string}] of this.cost) {
  //     if (
  //       new Decimal(value(step, optionIndex)).gt(
  //         (ri.get(key) as IValuable).value
  //       )
  //     ) {
  //       return false;
  //     }
  //   }
  //   return true;
  // };

  @property() override baseEffectString = '';

  cast(step: number, optionIndex: number) {
    this.perform(step, optionIndex);
  }

  @property() override perform: (step: number, optionIndex: number) => boolean =
    (step, optionIndex) => {
      const chanceRoll = Math.random();
      if (this.canAfford(step, optionIndex, 1)) {
        if (this.currentCooldown.lte(0.01)) {
          const beforePerform = this.beforePerform(
            step,
            optionIndex,
            chanceRoll
          );
          const logString = beforePerform.logString ?? '';
          const cost = beforePerform.cost ?? this.cost;
          const effect = beforePerform.effect ?? this.effect;
          ri.stats.intelligence.value = ri.stats.intelligence.value.plus(
            0.001 * this.level
          );
          // Reset cooldown
          this.currentCooldown = this.cooldown;
          // Update magic stats
          for (const tag of this.tags) {
            if (tag.includes('Magic')) ri.gain(tag, 0.01);
          }

          ri.logCostEffect(
            this,
            '',
            evaluateCost(cost, step, optionIndex),
            evaluateCost(effect, step, optionIndex),
            ['spell']
          );
          for (const [key, {value}] of cost) {
            ri.gain(key, -value(step, optionIndex));
          }
          // Perform the value-based effects.
          for (const [key, {value}] of effect) {
            ri.gain(key, value(step, optionIndex));
          }
          // Perform anything that can't be done in the value-based effects.
          this.performSpecial(step, optionIndex);
          this.uses = this.uses.plus(1);
          return true;
        } else {
          ri.log(
            `${this.displayName} is on cooldown ${formatTime(
              this.currentCooldown
            )} left`,
            ['spell', this.name]
          );
          return false;
        }
      } else {
        // Can't afford it.
        this.onCannotAfford(step, optionIndex);
        return false;
      }
      throw new Error('Something is wrong in this method.');
    };
  @property() override performSpecial: (
    step: number,
    optionIndex: number
  ) => void = (_step, _optionIndex) => {
    // Do nothing.
  };

  constructor() {
    super();
    setTimeout(() => {
      this.classList.add(this.name, ...this.tags);
    });
  }

  static override styles = [
    global_css,
    css`
      :host {
        min-width: 3em;
        text-align: center;
      }
      .container {
        border: 1px black solid;
        padding: 4px;
      }
      div.drag,
      div.drag-helper.dragging {
        background-color: rgb(var(--value-color, initial), 1);
        font-size: 1.5em;
        border: 1px solid black;
        border-radius: 4px;
        margin: 2px;
        padding: 2px 4px;
      }

      div.drag {
        transition-duration: 0.25s;
        cursor: move;
      }

      div.drag:hover {
        background-color: #0008;
        color: white;
      }

      div.drag-helper {
        display: none;
      }

      div.drag-helper.dragging {
        display: block;
        cursor: move;
      }

      .cooldown,
      .cannot-afford {
        opacity: 0.5;
      }
    `,
  ];
  override render() {
    return html`
      <juicy-draggable
        position=${'static'}
        dropselector=${'#activity-queue'}
        @juicy-draggable-stop=${this._onItemDropped}
      >
        <div
          class="chip spell fancy ${this.name}
          ${this.canAfford(-1, 0, 1, true) ? '' : 'cannot-afford'}
          ${this.currentCooldown.gt(0) ? 'cooldown' : ''}"
          @click=${this._onClick}
        >
          ${this.currentCooldown.gt(0)
            ? formatEng(this.currentCooldown, 1)
            : this.displayName}
          ${this.options.length > 0
            ? html`
                <select
                  name="options"
                  class=${this.options[this.optionIndex]}
                  @click=${(e: Event) => e.stopPropagation()}
                  @change=${(e: Event) =>
                    (this.optionIndex = Number(
                      (e.target as HTMLOptionElement).value
                    ))}
                >
                  ${this.options.map(
                    (option, index) => html`
                      <option
                        value=${index}
                        class=${this.options[index]}
                        ?selected=${index == this.optionIndex}
                      >
                        ${option}
                      </option>
                    `
                  )}
                </select>
              `
            : nothing}
        </div>
      </juicy-draggable>
    `;
  }

  override _updateTooltip() {
    let costEffectString;
    if (this.options.length > 0) {
      costEffectString = '<div class="options-container">';
      for (const optionName of this.options) {
        const option = this.optionData[optionName];
        costEffectString += `<br>${option.displayName}<br>${formatCostEffect(
          {...option, length: option.length ?? this.length},
          1
        )}<br>`;
      }
      costEffectString += '</div>';
    } else {
      costEffectString = formatCostEffect(this, 1);
    }
    this.tooltip?.setContent(
      `${this.description}<br><br>${costEffectString}${
        this.tooltipContent() ? '<br><br>' + this.tooltipContent() : ''
      }
      <br><br>Cooldown: ${formatTime(this.currentCooldown)}/${formatTime(
        this.cooldown
      )}
      <br>Uses: ${this.uses}<br>Time Spent: ${formatTime(
        this.timeSpent
      )}<br>Tags: ${[...this._tags]}`
    );
  }

  _onClick() {
    const event = new CustomEvent('spell-clicked', {
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }
  private override _onItemDropped(e: CustomEvent) {
    const drop = e.detail.dropElement;
    if (!drop) return;
    const event = new Event('spell-dropped', {
      bubbles: true,
      composed: true,
    });
    this.dispatchEvent(event);
  }
}
